/**
 * Each server's recording storages: live, through the relay, while the server is online; the
 * settings the cloud mirrors while it is offline.
 *
 * A `storages` change frame reloads the storages of the server it names. A `devices` change
 * frame does too, and starts a settling time: a camera's recording toggle moves the storage
 * state a second or two later, so the tab polls fast until then.
 */

import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms, snapshotMemberAtom } from '../snapshotAtoms';
import { api } from '@/api';
import { DEVICE_CHANGE_PROPAGATION_MS } from '@/types/devices';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import type {
    Storage, StorageEntry, StoragesSnapshot, StorageWithStatus,
} from '@/types/servers';
import { SnapshotCommand } from '@/types/snapshot';

export function getLiveEntries(storages: StorageWithStatus[]): StorageEntry[] {
    return storages.map((storage): StorageEntry => ({ isLive: true, storage }));
}

function getMirroredEntries(storages: Storage[]): StorageEntry[] {
    return storages.map((storage): StorageEntry => ({ isLive: false, storage }));
}

const NO_STORAGES: StoragesSnapshot = {
    entries: [], isLoaded: false, isLoading: true, error: null,
};

/** One server's storages as it reports them, with its last checks. */
export const liveStoragesAtom = atomFamily((serverId: string) => snapshotMemberAtom(
    snapshotAtoms<StoragesSnapshot>(NO_STORAGES, async (): Promise<StoragesSnapshot> => ({
        entries: getLiveEntries(await api.listStoragesWithStatus(serverId)),
        isLoaded: true,
        isLoading: false,
        error: null,
    })),
));

/** One server's storages as the cloud mirrors them, for while the server is offline. */
export const mirroredStoragesAtom = atomFamily((serverId: string) => snapshotMemberAtom(
    snapshotAtoms<StoragesSnapshot>(NO_STORAGES, async (): Promise<StoragesSnapshot> => ({
        entries: getMirroredEntries(await api.listServerStorages(serverId)),
        isLoaded: true,
        isLoading: false,
        error: null,
    })),
));

/** The storages a tab reads for `serverId`. */
export function storagesAtom(
    serverId: string, isOnline: boolean,
): ReturnType<typeof liveStoragesAtom> {
    return isOnline ? liveStoragesAtom(serverId) : mirroredStoragesAtom(serverId);
}

/** Until when, in ms since the epoch, a server's storages settle after a camera change. */
export const storagesPendingUntilAtom = atomFamily((_serverId: string) => atom(0));

const RELOAD = { command: SnapshotCommand.Reload } as const;

/** What the notifications stream means for the storages. */
export const storagesStreamAtom = atom(null, (_get, set, input: StreamInput): void => {
    // The gap may have hidden a change frame.
    if (input.kind === StreamInputKind.Reopened) {
        for (const serverId of liveStoragesAtom.getParams()) {
            set(liveStoragesAtom(serverId), RELOAD);
        }
        for (const serverId of mirroredStoragesAtom.getParams()) {
            set(mirroredStoragesAtom(serverId), RELOAD);
        }
        return;
    }
    if (input.kind !== StreamInputKind.Frame) {
        return;
    }
    const { frame } = input;
    if (frame.type !== 'change' ||
        (frame.resource !== 'storages' && frame.resource !== 'devices')) {
        return;
    }
    const { serverId } = frame;
    // Only an open tab has a member.
    if ([...liveStoragesAtom.getParams()].includes(serverId)) {
        set(liveStoragesAtom(serverId), RELOAD);
    }
    if ([...mirroredStoragesAtom.getParams()].includes(serverId)) {
        set(mirroredStoragesAtom(serverId), RELOAD);
    }
    if (frame.resource === 'devices') {
        set(storagesPendingUntilAtom(serverId), Date.now() + DEVICE_CHANGE_PROPAGATION_MS);
    }
});
